/**
 * Node Binding Utilities
 *
 * Tracks deleted node bindings so commit can propagate record deletions
 * to the connected external data source.
 */

import type * as Y from 'yjs';
import type { ShapeDataBinding } from '../models/ShapeDataBinding';

/**
 * Track a deleted node binding for later commit.
 * New records are not tracked because they do not yet exist remotely.
 */
export function trackDeletedNodeBinding(
  doc: Y.Doc,
  nodeId: string,
  binding: ShapeDataBinding
): void {
  if (binding.isNewRecord) return;
  const deletedMap = doc.getMap('deletedNodeBindings') as Y.Map<ShapeDataBinding>;
  deletedMap.set(nodeId, binding);
}

/**
 * Get deleted node bindings pending commit for a data source.
 */
export function getDeletedNodeBindings(
  doc: Y.Doc,
  sourceId: string
): Array<{ nodeId: string; binding: ShapeDataBinding }> {
  const deletedMap = doc.getMap('deletedNodeBindings') as Y.Map<ShapeDataBinding>;
  const result: Array<{ nodeId: string; binding: ShapeDataBinding }> = [];

  deletedMap.forEach((binding, nodeId) => {
    if (binding.sourceId === sourceId) {
      result.push({ nodeId, binding });
    }
  });

  return result;
}

/**
 * Clear deleted node bindings after successful commit or undo.
 */
export function clearDeletedNodeBindings(
  doc: Y.Doc,
  nodeIds: string[]
): void {
  const deletedMap = doc.getMap('deletedNodeBindings') as Y.Map<ShapeDataBinding>;
  for (const nodeId of nodeIds) {
    deletedMap.delete(nodeId);
  }
}

